import { useFocusEffect, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { Pressable, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { RatingWindow } from '@/api/partners/dashboard/use-dashboard-performance';
import {
  DASHBOARD_PERIOD_TIME_ZONE,
  getPerformancePeriod,
  useDashboardPerformance,
} from '@/api/partners/dashboard/use-dashboard-performance';
import { useAnimateWhenInView } from '@/core/hooks/use-animate-when-in-view';
import { notifyScrollVisibility } from '@/core/scroll-visibility';
import { ERROR_LIGHT, SUCCESS_DARK } from '@/style/colors';
import { Card, Text } from '@/ui';
import { ArrowDown } from '@/ui/icons/arrow-down';
import { ArrowUp } from '@/ui/icons/arrow-up';
import { LineChart } from '@/ui/line-chart';
import { Loader } from '@/ui/loader';

import { CarRatingsTable } from './car-ratings-table';
import {
  DASHBOARD_RATING_BREAKDOWN_GOOD_THRESHOLD,
  DASHBOARD_RATING_CRITICAL_THRESHOLD,
  formatChartMonth,
  formatChartMonthWithYear,
  formatCompactLineChartLabel,
  formatDashboardRatingScore,
} from './dashboard-formatters';

const STAR_PATH =
  'M12 0L14.6942 8.2918H23.4127L16.3593 13.4164L19.0534 21.7082L12 16.5836L4.94658 21.7082L7.64074 13.4164L0.587322 8.2918H9.30583L12 0Z';

const LOW_RATED_CAR_MAX_OVERALL = 3;

const BREAKDOWN_FIELDS = [
  {
    key: 'pickupAndReturnRating',
    label: 'Pickup and return',
  },
  {
    key: 'carConditionRating',
    label: 'Car condition',
  },
  {
    key: 'cleanlinessRating',
    label: 'Cleanliness',
  },
] as const;

const DISTRIBUTION_ORDER = ['5', '4', '3', '2', '1'] as const;
const DISTRIBUTION_PLOT_HEIGHT = 160;
const COMPACT_HISTOGRAM_HEIGHT = 215;
const COMPACT_DISTRIBUTION_BAR_MAX_HEIGHT = 141;

function breakdownFillColor({
  score,
  accentColor,
  warningColor,
  errorColor,
}: {
  score: number | null;
  accentColor: string;
  warningColor: string;
  errorColor: string;
}) {
  if (score === null) {
    return accentColor;
  }

  if (score < DASHBOARD_RATING_CRITICAL_THRESHOLD) {
    return errorColor;
  }

  if (score < DASHBOARD_RATING_BREAKDOWN_GOOD_THRESHOLD) {
    return warningColor;
  }

  return accentColor;
}

type RatingsByMonthEntry = {
  month: string;
  averageScore: number | null;
  reviewCount: number;
};

function ratingsMonthKey(month: string) {
  return month.slice(0, 7);
}

function ratingScoreForChart(entry: RatingsByMonthEntry | undefined) {
  if (!entry || entry.reviewCount <= 0) {
    return null;
  }

  if (entry.averageScore == null || !Number.isFinite(entry.averageScore)) {
    return null;
  }

  return entry.averageScore;
}

function ratingsByMonthForPeriod(
  byMonth: RatingsByMonthEntry[],
  monthCount: number,
  periodFrom?: string,
  periodTimeZone: string = DASHBOARD_PERIOD_TIME_ZONE
) {
  const from = periodFrom ?? getPerformancePeriod(monthCount).from;
  const start = DateTime.fromISO(from, {
    zone: periodTimeZone,
  }).startOf('month');
  const lookup = new Map(
    byMonth.map((entry) => [ratingsMonthKey(entry.month), entry])
  );

  return Array.from({ length: monthCount }, (_, index) => {
    const month = start.plus({ months: index }).toFormat('yyyy-MM');
    const entry = lookup.get(month);

    return {
      month,
      averageScore: ratingScoreForChart(entry),
      reviewCount: entry?.reviewCount ?? 0,
    };
  });
}

function previousPeriodLabel(monthCount: number) {
  return monthCount === 1
    ? 'vs previous month'
    : `vs previous ${monthCount} months`;
}

function formatScoreChange({
  current,
  previous,
  monthCount,
}: {
  current: number | null;
  previous: number | null;
  monthCount: number;
}) {
  if (current === null || previous === null) {
    return null;
  }

  const delta = current - previous;
  if (Math.abs(delta) < 0.05) {
    return null;
  }

  const formatted = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);

  return {
    magnitude: formatted,
    periodLabel: previousPeriodLabel(monthCount),
    improved: delta > 0,
  };
}

function formatCountChange({
  current,
  previous,
  monthCount,
}: {
  current: number;
  previous: number;
  monthCount: number;
}) {
  const delta = current - previous;
  if (delta === 0) {
    return null;
  }

  const formatted =
    delta > 0 ? `+${delta.toLocaleString()}` : delta.toLocaleString();

  return {
    magnitude: formatted,
    periodLabel: previousPeriodLabel(monthCount),
    improved: delta > 0,
  };
}

function formatSubscoreChange({
  current,
  previous,
  monthCount,
}: {
  current: number | null;
  previous: number | null;
  monthCount: number;
}) {
  if (current === null || previous === null) {
    return null;
  }

  const delta = current - previous;
  if (Math.abs(delta) < 0.05) {
    return null;
  }

  const formatted = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);

  return {
    magnitude: formatted,
    periodLabel: previousPeriodLabel(monthCount),
    improved: delta > 0,
  };
}

type RatingsViewProps = {
  monthCount: number;
};

function AnimatedDistributionBar({
  percent,
  barHeightPercent,
  color,
  shouldAnimate,
}: {
  percent: number;
  barHeightPercent: number;
  color: string;
  shouldAnimate: boolean;
}) {
  const { styles } = useStyles(stylesheet);
  const progress = useSharedValue(0);
  const targetHeight = (barHeightPercent / 100) * DISTRIBUTION_PLOT_HEIGHT;

  useEffect(() => {
    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [barHeightPercent, progress, shouldAnimate]);

  const animatedBarStyle = useAnimatedStyle(() => ({
    height: targetHeight * progress.value,
  }));

  const animatedLabelStyle = useAnimatedStyle(() => ({
    bottom: targetHeight * progress.value,
  }));

  if (percent === 0) {
    return (
      <>
        <View
          style={[
            styles.distributionBar,
            styles.distributionBarZero,
            { backgroundColor: color },
          ]}
        />
        <View
          style={[
            styles.distributionBarLabelWrap,
            styles.distributionBarLabelAtZero,
          ]}
        >
          <Text type="caption1" style={styles.distributionBarLabel}>
            0%
          </Text>
        </View>
      </>
    );
  }

  return (
    <>
      <Animated.View
        style={[
          styles.distributionBar,
          animatedBarStyle,
          { backgroundColor: color },
        ]}
      />
      <Animated.View
        style={[styles.distributionBarLabelWrap, animatedLabelStyle]}
      >
        <Text type="caption1" style={styles.distributionBarLabel}>
          {`${percent}%`}
        </Text>
      </Animated.View>
    </>
  );
}

function CompactDistributionColumn({
  star,
  percent,
  color,
  shouldAnimate,
}: {
  star: string;
  percent: number;
  color: string;
  shouldAnimate: boolean;
}) {
  const { styles } = useStyles(stylesheet);
  const progress = useSharedValue(0);
  const targetHeight =
    percent === 0
      ? 1
      : Math.max(2, (percent / 100) * COMPACT_DISTRIBUTION_BAR_MAX_HEIGHT);

  useEffect(() => {
    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [percent, progress, shouldAnimate, targetHeight]);

  const animatedBarStyle = useAnimatedStyle(() => ({
    height: targetHeight * progress.value,
  }));

  return (
    <View style={styles.compactDistributionColumn}>
      <Text type="caption1" style={styles.secondary}>{`${percent}%`}</Text>
      <Animated.View
        style={[
          styles.compactDistributionBar,
          animatedBarStyle,
          { backgroundColor: color },
        ]}
      />
      <Text type="caption1" style={styles.secondary}>{`${star} ★`}</Text>
    </View>
  );
}

function AnimatedBreakdownFill({
  fillPercent,
  backgroundColor,
  shouldAnimate,
}: {
  fillPercent: number;
  backgroundColor: string;
  shouldAnimate: boolean;
}) {
  const { styles } = useStyles(stylesheet);
  const progress = useSharedValue(0);

  useEffect(() => {
    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [fillPercent, progress, shouldAnimate]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scaleX: progress.value }],
  }));

  return (
    <Animated.View
      style={[
        styles.breakdownFill,
        {
          width: `${fillPercent}%`,
          backgroundColor,
          transformOrigin: 'left center',
        },
        animatedStyle,
      ]}
    />
  );
}

export function RatingsView({ monthCount }: RatingsViewProps) {
  const { styles, theme } = useStyles(stylesheet);
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const distributionInView = useAnimateWhenInView();
  const breakdownInView = useAnimateWhenInView();
  const chartAccent = theme.colors.histogram.active.backgroundColor;
  const chartWarning = theme.colors.warning;
  const chartError = theme.colors.error;
  const chartTrack = theme.colors.progressIndicator.inactive.backgroundColor;
  const router = useRouter();
  const openAllRatings = useCallback(() => {
    router.push({
      pathname: '/partner/dashboard/car-ratings',
      params: { months: String(monthCount) },
    });
  }, [monthCount, router]);
  const requestPeriod = getPerformancePeriod(monthCount);
  const performance = useDashboardPerformance({
    variables: { from: requestPeriod.from, to: requestPeriod.to },
  });

  useFocusEffect(
    useCallback(() => {
      notifyScrollVisibility();
    }, [])
  );

  const data = performance.data;
  const reviewPeriod = {
    from: data?.period.from ?? requestPeriod.from,
    to: data?.period.to ?? requestPeriod.to,
    timeZone: data?.period.timeZone,
  };
  const ratings = data?.ratings;
  const current = ratings?.current;
  const previous = ratings?.previous;
  const reviewCount = current?.reviewCount ?? 0;
  const hasRatings = reviewCount > 0;

  const overallChange = formatScoreChange({
    current: current?.averageScore ?? null,
    previous: previous?.averageScore ?? null,
    monthCount,
  });
  const reviewsChange = formatCountChange({
    current: current?.reviewCount ?? 0,
    previous: previous?.reviewCount ?? 0,
    monthCount,
  });

  const distributionTotal = DISTRIBUTION_ORDER.reduce(
    (sum, key) => sum + (ratings?.distribution[key] ?? 0),
    0
  );
  const ratingsByMonth = useMemo(
    () =>
      ratingsByMonthForPeriod(
        ratings?.byMonth ?? [],
        monthCount,
        data?.period.from,
        data?.period.timeZone
      ),
    [data?.period.from, data?.period.timeZone, monthCount, ratings?.byMonth]
  );

  const satisfactionChartMin = useMemo(() => {
    const monthScores = ratingsByMonth
      .map((month) => month.averageScore)
      .filter((score): score is number => score !== null);

    if (monthScores.length === 0) {
      return undefined;
    }

    const dataMin = Math.min(...monthScores);
    return Math.max(1, Math.floor(dataMin));
  }, [ratingsByMonth]);

  const lowRatedCars = useMemo(
    () =>
      (data?.lowRatedCars ?? [])
        .filter(
          (car) =>
            car.averageScore !== null &&
            car.averageScore <= LOW_RATED_CAR_MAX_OVERALL
        )
        .sort(
          (left, right) => (left.averageScore ?? 0) - (right.averageScore ?? 0)
        ),
    [data?.lowRatedCars]
  );

  return (
    <View style={styles.container}>
      {performance.isLoading ? (
        <Loader style={styles.loader} />
      ) : !data || !hasRatings ? (
        <Card style={styles.empty}>
          <Text type="bodyBold">Guest Ratings</Text>
          <View style={styles.emptyContent}>
            <Text type="bodyRegular">No reviews yet</Text>
            <Text type="caption1" style={styles.secondary}>
              Ratings show here after guests rate their trips.
            </Text>
          </View>
        </Card>
      ) : (
        <>
          <Card
            style={
              isCompactLayout
                ? [styles.card, styles.guestRatingsCardCompact]
                : styles.card
            }
          >
            <View style={styles.guestRatingsHeader}>
              <Text type={isCompactLayout ? 'title2' : 'bodyBold'}>
                {isCompactLayout ? 'Guest Ratings' : 'Customer satisfaction'}
              </Text>
              <Pressable onPress={openAllRatings}>
                <Text type="bodyRegular" style={styles.link}>
                  View all ratings
                </Text>
              </Pressable>
            </View>
            <View
              style={
                isCompactLayout
                  ? styles.summaryMetricsCompact
                  : styles.summaryMetrics
              }
            >
              <View
                style={
                  isCompactLayout
                    ? styles.summaryMetricCompact
                    : styles.summaryMetric
                }
              >
                <Text type="caption1" style={styles.secondary}>
                  {isCompactLayout ? 'Overall rating' : 'Overall trip rating'}
                </Text>
                <View
                  style={
                    isCompactLayout ? styles.scoreRowCompact : styles.scoreRow
                  }
                >
                  <Text type={isCompactLayout ? 'largeTitle' : 'title1'}>
                    {formatDashboardRatingScore(current?.averageScore ?? null)}
                  </Text>
                  <StarRow score={current?.averageScore ?? 0} />
                </View>
                {overallChange ? (
                  <MetricChange
                    change={overallChange}
                    higherIsBetter
                    variant={isCompactLayout ? 'compact' : 'default'}
                  />
                ) : null}
              </View>
              <View
                style={
                  isCompactLayout
                    ? styles.summaryMetricCompact
                    : styles.summaryMetric
                }
              >
                <Text type="caption1" style={styles.secondary}>
                  {isCompactLayout ? 'Rated trips' : 'Reviews'}
                </Text>
                <Text type={isCompactLayout ? 'largeTitle' : 'title1'}>
                  {(current?.reviewCount ?? 0).toLocaleString()}
                </Text>
                {reviewsChange ? (
                  <MetricChange
                    change={reviewsChange}
                    higherIsBetter
                    variant={isCompactLayout ? 'compact' : 'default'}
                  />
                ) : null}
              </View>
            </View>
            <LineChart
              key={`${monthCount}-${data.period.from}`}
              layout={isCompactLayout ? 'compact' : 'default'}
              points={ratingsByMonth.map((month, index) => ({
                label: isCompactLayout
                  ? formatCompactLineChartLabel(month.month, index)
                  : formatChartMonth(month.month),
                detailLabel: isCompactLayout
                  ? formatChartMonthWithYear(month.month)
                  : undefined,
                value: month.averageScore,
              }))}
              formatValue={(value) => value.toFixed(1)}
              formatAxis={(value) => value.toFixed(1)}
              strokeColor={chartAccent}
              minValue={satisfactionChartMin}
              maxValue={5}
              integerAxisTicks
            />
          </Card>

          <View style={styles.split}>
            <Card
              style={[
                styles.card,
                styles.splitCard,
                isCompactLayout ? styles.distributionCardCompact : undefined,
              ]}
            >
              <Text type={isCompactLayout ? 'title2' : 'bodyBold'}>
                {isCompactLayout
                  ? 'Rating Distribution'
                  : 'Rating distribution'}
              </Text>
              {isCompactLayout ? (
                <View
                  ref={distributionInView.ref}
                  onLayout={distributionInView.onLayout}
                  style={styles.compactHistogram}
                >
                  {DISTRIBUTION_ORDER.map((key) => {
                    const count = ratings?.distribution[key] ?? 0;
                    const percent =
                      distributionTotal === 0
                        ? 0
                        : Math.round((count / distributionTotal) * 100);

                    return (
                      <CompactDistributionColumn
                        key={key}
                        star={key}
                        percent={percent}
                        color={chartAccent}
                        shouldAnimate={distributionInView.isInView}
                      />
                    );
                  })}
                </View>
              ) : (
                <View style={styles.distributionChart}>
                  <View style={styles.distributionPlotRow}>
                    <View style={styles.distributionYAxis}>
                      {[100, 50, 0].map((tick) => (
                        <Text
                          key={tick}
                          type="caption2"
                          style={styles.secondary}
                        >
                          {`${tick}%`}
                        </Text>
                      ))}
                    </View>
                    <View style={styles.distribution}>
                      {DISTRIBUTION_ORDER.map((key) => {
                        const count = ratings?.distribution[key] ?? 0;
                        const percent =
                          distributionTotal === 0
                            ? 0
                            : Math.round((count / distributionTotal) * 100);

                        const barHeightPercent =
                          percent > 0 ? Math.max(percent, 2) : 0;

                        return (
                          <View key={key} style={styles.distributionColumn}>
                            <View style={styles.distributionTrack}>
                              <AnimatedDistributionBar
                                percent={percent}
                                barHeightPercent={barHeightPercent}
                                color={chartAccent}
                                shouldAnimate
                              />
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                  <View style={styles.distributionXRow}>
                    <View style={styles.distributionYAxisSpacer} />
                    <View style={styles.distributionXLabels}>
                      {DISTRIBUTION_ORDER.map((key) => (
                        <Text
                          key={key}
                          type="caption1"
                          style={[styles.secondary, styles.distributionXLabel]}
                        >
                          {`${key} ★`}
                        </Text>
                      ))}
                    </View>
                  </View>
                </View>
              )}
            </Card>

            <Card style={[styles.card, styles.splitCard]}>
              <Text type="bodyBold">Rating breakdown</Text>
              <View
                ref={isCompactLayout ? breakdownInView.ref : undefined}
                onLayout={
                  isCompactLayout ? breakdownInView.onLayout : undefined
                }
                style={styles.breakdown}
              >
                {BREAKDOWN_FIELDS.map((field) => {
                  const currentScore =
                    current?.[field.key as keyof RatingWindow] ?? null;
                  const previousScore =
                    previous?.[field.key as keyof RatingWindow] ?? null;
                  const fillPercent =
                    currentScore === null
                      ? 0
                      : Math.min(100, (currentScore / 5) * 100);
                  const change = formatSubscoreChange({
                    current:
                      typeof currentScore === 'number' ? currentScore : null,
                    previous:
                      typeof previousScore === 'number' ? previousScore : null,
                    monthCount,
                  });

                  return (
                    <View key={field.key} style={styles.breakdownRow}>
                      <View style={styles.breakdownHeader}>
                        <Text type="bodyRegular">{field.label}</Text>
                        <Text type="bodyRegular">
                          {formatDashboardRatingScore(
                            typeof currentScore === 'number'
                              ? currentScore
                              : null
                          )}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.breakdownTrack,
                          { backgroundColor: chartTrack },
                        ]}
                      >
                        <AnimatedBreakdownFill
                          fillPercent={fillPercent}
                          backgroundColor={breakdownFillColor({
                            score:
                              typeof currentScore === 'number'
                                ? currentScore
                                : null,
                            accentColor: chartAccent,
                            warningColor: chartWarning,
                            errorColor: chartError,
                          })}
                          shouldAnimate={
                            isCompactLayout ? breakdownInView.isInView : true
                          }
                        />
                      </View>
                      {change ? (
                        <MetricChange change={change} higherIsBetter />
                      ) : null}
                    </View>
                  );
                })}
              </View>
            </Card>
          </View>

          {lowRatedCars.length > 0 ? (
            <CarRatingsTable
              cars={lowRatedCars}
              period={reviewPeriod}
              onViewAll={openAllRatings}
              onOpenReservation={(reservationId) => {
                router.push(`/partner/reservations/${reservationId}`);
              }}
            />
          ) : null}
        </>
      )}
    </View>
  );
}

function MetricChange({
  change,
  higherIsBetter,
  variant = 'default',
}: {
  change: {
    magnitude: string;
    periodLabel: string;
    improved: boolean;
  };
  higherIsBetter: boolean;
  variant?: 'default' | 'compact';
}) {
  const { styles } = useStyles(stylesheet);
  const positive = higherIsBetter ? change.improved : !change.improved;
  const magnitude =
    variant === 'compact'
      ? change.magnitude.replace(/^\+/, '')
      : change.magnitude;

  return (
    <View style={styles.metricChange}>
      <View style={styles.metricChangeIcon}>
        {positive ? (
          <ArrowUp width={10} height={12} color={SUCCESS_DARK} />
        ) : (
          <ArrowDown width={10} height={12} color={ERROR_LIGHT} />
        )}
      </View>
      <Text type="caption1">
        <Text
          type={variant === 'compact' ? 'caption1Bold' : 'caption1'}
          style={positive ? styles.changeGood : styles.changeBad}
        >
          {magnitude}
        </Text>
        <Text type="caption1" style={styles.secondary}>
          {` ${change.periodLabel}`}
        </Text>
      </Text>
    </View>
  );
}

function StarRow({ score }: { score: number }) {
  const { theme } = useStyles(stylesheet);
  const filledCount = Math.round(score);

  return (
    <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
      {Array.from({ length: 5 }, (_, index) => (
        <Svg key={index} width={12} height={12} viewBox="0 0 24 24">
          <Path
            d={STAR_PATH}
            fill={
              index < filledCount
                ? theme.colors.text.primary
                : theme.colors.card.default.borderColor
            }
          />
        </Svg>
      ))}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    gap: 24,
  },
  loader: {
    height: 240,
    alignSelf: 'center',
  },
  empty: {
    padding: 24,
    gap: 16,
  },
  emptyContent: {
    gap: 4,
  },
  secondary: {
    color: theme.colors.text.secondary,
  },
  guestRatingsHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  link: {
    textDecorationLine: 'underline',
  },
  card: {
    padding: 16,
    gap: 16,
  },
  guestRatingsCardCompact: {
    gap: 24,
  },
  distributionCardCompact: {
    gap: 24,
  },
  compactHistogram: {
    flexDirection: 'row',
    gap: 16,
    height: COMPACT_HISTOGRAM_HEIGHT,
    width: '100%',
  },
  compactDistributionColumn: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  compactDistributionBar: {
    width: '100%',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  summaryMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  summaryMetricsCompact: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  summaryMetric: {
    gap: 4,
    minWidth: 140,
  },
  summaryMetricCompact: {
    gap: 8,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scoreRowCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metricChange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricChangeIcon: {
    width: 10,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -2,
  },
  changeGood: {
    color: SUCCESS_DARK,
  },
  changeBad: {
    color: ERROR_LIGHT,
  },
  split: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 16,
  },
  splitCard: {
    flex: {
      md: 1,
    },
    width: {
      xs: '100%',
    },
    minWidth: {
      md: 280,
    },
    alignSelf: 'stretch',
  },
  distributionChart: {
    flex: 1,
    width: '100%',
    paddingTop: 8,
    justifyContent: 'flex-end',
  },
  distributionPlotRow: {
    flexDirection: 'row',
    gap: 12,
  },
  distributionYAxis: {
    width: 40,
    height: DISTRIBUTION_PLOT_HEIGHT,
    paddingRight: 18,
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  distributionYAxisSpacer: {
    width: 52,
  },
  distribution: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    height: DISTRIBUTION_PLOT_HEIGHT,
  },
  distributionColumn: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    overflow: 'visible',
  },
  distributionXRow: {
    flexDirection: 'row',
    gap: 18,
    marginTop: 8,
  },
  distributionXLabels: {
    flex: 1,
    flexDirection: 'row',
    gap: 8,
  },
  distributionXLabel: {
    flex: 1,
    textAlign: 'center',
  },
  distributionTrack: {
    flex: 1,
    width: '100%',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  distributionBar: {
    width: '100%',
    borderRadius: 2,
  },
  distributionBarZero: {
    height: 1,
  },
  distributionBarLabelWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    marginBottom: 8,
    alignItems: 'center',
  },
  distributionBarLabel: {
    textAlign: 'center',
  },
  distributionBarLabelAtZero: {
    bottom: 1,
  },
  breakdown: {
    gap: 20,
    flex: {
      md: 1,
    },
    justifyContent: {
      md: 'space-between',
    },
  },
  breakdownRow: {
    gap: 8,
  },
  breakdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  breakdownTrack: {
    height: 8,
    borderRadius: 8,
    overflow: 'hidden',
  },
  breakdownFill: {
    height: '100%',
    borderRadius: 8,
  },
}));
